import React from 'react';
import { Button, Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import '../../styles/integrations.css';
import { logoFor } from '../../constants/integrationLogos';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';

export const STATUS_META = Object.freeze({
    connected: { label: 'Connected', cls: 'int-pill-connected' },
    none: { label: 'Not connected', cls: 'int-pill-none' },
    attention: { label: 'Needs attention', cls: 'int-pill-attention' },
});

export const StatusPill = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.none;
    return <span className={`int-pill ${meta.cls}`}>{meta.label}</span>;
};

/** Credential `status` from the server -> pill. ACTIVE/CONNECTED are healthy; any other stored state needs a look. */
export function statusFromCredential(cred) {
    if (!cred) return 'none';
    const s = String(cred.status || '').toUpperCase();
    return s === 'ACTIVE' || s === 'CONNECTED' ? 'connected' : 'attention';
}

/** Brand mark on a light tile (readable in both themes), or a neutral initial when we hold no genuine asset. */
export const LogoTile = ({ logoKey, name }) => {
    const src = logoFor(logoKey);
    if (src) {
        return (
            <span className="int-logo" aria-hidden="true">
                <img src={src} alt="" />
            </span>
        );
    }
    return <span className="int-logo int-logo-letter" aria-hidden="true">{(name || '?').trim().charAt(0).toUpperCase()}</span>;
};

/**
 * One integration tile. The whole card opens the configuration drawer (mouse); the labelled button at the bottom is the
 * keyboard/screen-reader path. `menu` is `[{ key, label, danger, onSelect }]` for the kebab.
 */
export const IntegrationCard = ({ cardKey, name, purpose, logoKey, status, updatedAt, note, menu = [], onOpen }) => {
    const label = status === 'none' ? 'Set up' : 'Configure';
    const items = [
        { key: 'configure', label: 'Configure', onSelect: onOpen },
        ...menu,
    ];
    return (
        <div className="int-card" data-card={cardKey || name} onClick={onOpen}>
            <div className="int-card-head">
                <LogoTile logoKey={logoKey} name={name} />
                <span className="int-card-name">{name}</span>
                <span onClick={(e) => e.stopPropagation()}>
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: items.map((i) => ({ key: i.key, label: i.label, danger: !!i.danger })),
                            onClick: ({ key, domEvent }) => {
                                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                                const hit = items.find((i) => i.key === key);
                                if (hit) hit.onSelect();
                            },
                        }}
                    >
                        <Button type="text" size="small" icon={<MoreOutlined />} aria-label={`More actions for ${name}`} />
                    </Dropdown>
                </span>
            </div>
            <div className="int-card-purpose">{purpose}</div>
            <div className="int-card-meta">{updatedAt ? `Updated ${formatDdMmYyyy(updatedAt)}` : (note || 'Never configured')}</div>
            <div className="int-card-foot">
                <StatusPill status={status} />
                <Button type={status === 'none' ? 'primary' : 'default'} size="small" aria-label={`${label} ${name}`} onClick={onOpen}>{label}</Button>
            </div>
        </div>
    );
};

export const CardGrid = ({ children }) => <div className="int-grid">{children}</div>;
